/**
 * Address formatting utilities for consistent address display
 */

export interface AddressInfo {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  floor?: number | string | null;
}

/** True for a floor that should be displayed: 0 is a real floor (ground), '' is not. */
const hasFloor = (floor?: number | string | null): boolean =>
  floor !== null && floor !== undefined && `${floor}`.trim() !== '';

/**
 * Formats an address object into a readable string
 * @param address - Address object with street, city, state, zip, country, floor
 * @param floorLabel - Localised word for "floor"; when omitted the floor is not shown
 * @returns Formatted address string or '-' if no address data
 */
export const formatAddress = (address?: AddressInfo, floorLabel?: string): string => {
  if (!address) return '-';

  const { street, city, state, zip, country, floor } = address;

  // Return '-' if no address components are available. A floor on its own is not an
  // address — "Floor 3" with no street tells nobody where to go.
  if (!street && !city && !state && !zip && !country) {
    return '-';
  }

  const addressParts: string[] = [];

  // Add street if available
  if (street) {
    addressParts.push(street);
  }

  // Add city, state, zip combination
  const locationParts: string[] = [];
  if (city) locationParts.push(city);
  if (state) locationParts.push(state);
  if (zip) locationParts.push(zip);

  if (locationParts.length > 0) {
    addressParts.push(locationParts.join(' '));
  }

  // Add country if available and different from default
  if (country && country.toLowerCase() !== 'usa' && country.toLowerCase() !== 'us') {
    addressParts.push(country);
  }

  // Last, so it reads the way it is said: "Δελφών 11, Θεσσαλονίκη 54621, Όροφος 3".
  if (floorLabel && hasFloor(floor)) {
    addressParts.push(`${floorLabel} ${floor}`);
  }

  return addressParts.length > 0 ? addressParts.join(', ') : '-';
};

/**
 * Gets address information with fallback logic
 * Tries primary address first, then falls back to secondary address
 *
 * The FLOOR falls back on its own, independently of the rest. A visit carries its own
 * street and city but its floor is only populated for visits generated after
 * 2026-08-10 — before that the column was never written — so an all-or-nothing
 * fallback would show the visit's address and silently drop a floor the client does
 * have. This mirrors the mobile app's `visit.floor ?? visit.client?.floor`.
 *
 * @param primaryAddress - Primary address object
 * @param fallbackAddress - Fallback address object
 * @param floorLabel - Localised word for "floor"; when omitted the floor is not shown
 * @returns Best available address or '-'
 */
export const getAddressWithFallback = (
  primaryAddress?: AddressInfo,
  fallbackAddress?: AddressInfo,
  floorLabel?: string
): string => {
  const floor = hasFloor(primaryAddress?.floor) ? primaryAddress?.floor : fallbackAddress?.floor;

  // Try primary address first
  const primaryFormatted = formatAddress(primaryAddress && { ...primaryAddress, floor }, floorLabel);
  if (primaryFormatted !== '-') {
    return primaryFormatted;
  }

  // Fall back to secondary address
  return formatAddress(fallbackAddress && { ...fallbackAddress, floor }, floorLabel);
};
